import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { labApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import Header from '../components/layout/Header';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import {
  FlaskConical, Sparkles, Zap, Loader2, RefreshCw, Calendar, AlertTriangle,
  Home, Truck, MapPin, Send, CheckCircle2, X, Plus, ChevronRight,
  TrendingUp, Settings as SettingsIcon, Lightbulb
} from 'lucide-react';
import { cn } from '../lib/utils';

const KNOWN_LABS = ['IGI', 'SGL', 'DHC', 'IDT', 'GSI'];

const LAB_GRADIENT = {
  IGI: 'from-violet-500 to-indigo-600',
  SGL: 'from-amber-500 to-orange-600',
  DHC: 'from-emerald-500 to-teal-600',
  IDT: 'from-cyan-500 to-blue-600',
  GSI: 'from-rose-500 to-pink-600',
};
const LAB_RING = {
  IGI: 'ring-violet-300',
  SGL: 'ring-amber-300',
  DHC: 'ring-emerald-300',
  IDT: 'ring-cyan-300',
  GSI: 'ring-rose-300',
};
const FALLBACK_GRAD = 'from-slate-500 to-slate-700';

const PRIORITY_BADGE = {
  urgent:  { label: '🔴 URGENT',  bg: 'bg-red-50 dark:bg-red-500/15', text: 'text-red-700 dark:text-red-300', dot: 'bg-red-500' },
  high:    { label: '🟠 HIGH',    bg: 'bg-orange-50 dark:bg-orange-500/15', text: 'text-orange-700 dark:text-orange-300', dot: 'bg-orange-500' },
  medium:  { label: '🟡 MEDIUM',  bg: 'bg-amber-50 dark:bg-amber-500/15', text: 'text-amber-700 dark:text-amber-300', dot: 'bg-amber-500' },
  routine: { label: '🟢 ROUTINE', bg: 'bg-emerald-50 dark:bg-emerald-500/15', text: 'text-emerald-700 dark:text-emerald-300', dot: 'bg-emerald-500' },
};

// ─── AI Recommendations ──────────────────────────────────────
const Recommendations = ({ onCreateBooking }) => {
  const navigate = useNavigate();
  const [data, setData] = useState({ recommendations: [], status: 'idle' });
  const [loading, setLoading] = useState(true);
  const [regenerating, setRegenerating] = useState(false);

  const fetchRecs = useCallback(() => {
    setLoading(true);
    labApi.recommendations()
      .then(r => setData(r.data))
      .catch(() => toast.error('Failed to load recommendations'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(fetchRecs, [fetchRecs]);

  // Poll while status='generating'
  useEffect(() => {
    if (data?.status !== 'generating') return;
    let polls = 0;
    const interval = setInterval(async () => {
      polls += 1;
      try {
        const r = await labApi.recommendations();
        setData(r.data);
        if (r.data?.status === 'ready') {
          setRegenerating(false);
          toast.success(`${(r.data.recommendations || []).length} recommendations ready`);
        } else if (r.data?.status === 'failed') {
          setRegenerating(false);
          toast.error(r.data.error || 'Generation failed — try again');
        }
      } catch { /* swallow */ }
      if (polls >= 25 && data?.status === 'generating') {
        setRegenerating(false);
        toast.error('Generation taking too long — auto-recovers in ~5 min, retry then.');
        clearInterval(interval);
      }
    }, 4000);
    return () => clearInterval(interval);
  }, [data?.status]);

  const regenerate = async () => {
    setRegenerating(true);
    toast.message('Asking Claude to analyse your lab pipeline… ~30s');
    try {
      const r = await labApi.regenerate();
      if (r.data?.status === 'generating') {
        setData(d => ({ ...d, status: 'generating', started_at: r.data.started_at }));
      }
    } catch (e) {
      setRegenerating(false);
      toast.error(e?.response?.data?.detail || 'Failed to start');
    }
  };

  const isGenerating = data?.status === 'generating' || regenerating;
  const recs = data.recommendations || [];

  return (
    <div className="space-y-4">
      {/* Hero */}
      <Card className="overflow-hidden bg-gradient-to-br from-violet-500 via-indigo-500 to-blue-500 border-0">
        <CardContent className="p-4 md:p-6 text-white">
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3 md:gap-4">
            <div className="min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <Sparkles className="h-4 w-4" />
                <span className="text-xs uppercase tracking-[0.2em] font-semibold opacity-90">AI Lab Planner</span>
              </div>
              <h2 className="font-heading text-xl md:text-3xl font-bold leading-tight">
                {isGenerating ? 'Analysing your lab pipeline…'
                  : data?.status === 'failed' ? 'Generation failed'
                  : recs.length > 0 ? `${recs.filter(r => r.type !== 'wait').length} action${recs.filter(r => r.type !== 'wait').length === 1 ? '' : 's'} recommended`
                  : 'Click Generate to plan your labs'}
              </h2>
              <p className="text-xs md:text-sm opacity-90 mt-1">
                {isGenerating
                  ? 'Claude is reading production stages, deadlines, and lab thresholds — usually 30-45 seconds.'
                  : data?.status === 'failed'
                  ? `Last run errored: ${(data.error || '').slice(0, 140)}. Click Regenerate to retry.`
                  : data.generated_at
                  ? `Last refreshed ${new Date(data.generated_at).toLocaleString('en-IN', { hour: '2-digit', minute: '2-digit', day: '2-digit', month: 'short' })} · Claude Sonnet 4.5`
                  : 'No recommendations yet — let Claude analyse your pipeline.'}
              </p>
            </div>
            <Button
              onClick={regenerate}
              disabled={isGenerating}
              className="bg-white/20 hover:bg-white/30 backdrop-blur-sm border border-white/30 text-white font-semibold shadow-lg shadow-black/20 w-full md:w-auto flex-shrink-0"
              data-testid="lab-regenerate-btn"
            >
              {isGenerating ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Zap className="h-4 w-4 mr-1.5" />}
              {isGenerating ? 'Generating…' : recs.length ? 'Regenerate' : 'Generate'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Recommendations */}
      {loading ? (
        <div className="grid gap-3">{[1,2,3].map(i => <Skeleton key={i} className="h-32 w-full" />)}</div>
      ) : recs.length === 0 ? (
        <Card><CardContent className="p-12 text-center">
          <Sparkles className="h-12 w-12 mx-auto text-violet-400 mb-3" />
          <p className="text-sm text-muted-foreground">Click <strong>Generate</strong> above. Claude will analyse production stages, deadlines, and thresholds across all labs.</p>
        </CardContent></Card>
      ) : (
        <div className="grid gap-3">
          {recs.map(rec => <RecommendationCard key={rec.id} rec={rec} onCreateBooking={onCreateBooking} navigate={navigate} />)}
        </div>
      )}
    </div>
  );
};

const RecommendationCard = ({ rec, onCreateBooking, navigate }) => {
  const pri = PRIORITY_BADGE[rec.priority] || PRIORITY_BADGE.routine;
  const grad = LAB_GRADIENT[rec.lab] || FALLBACK_GRAD;
  const isAction = rec.type === 'in_house' || rec.type === 'send_out';
  const typeIcon = rec.type === 'in_house' ? Home : rec.type === 'send_out' ? Truck : Lightbulb;
  const TypeIcon = typeIcon;
  const typeLabel = rec.type === 'in_house' ? 'In-house visit' : rec.type === 'send_out' ? 'Send out' : 'Wait';

  return (
    <Card
      data-testid={`lab-rec-${rec.lab}-${rec.type}`}
      className={cn(
        'overflow-hidden border-l-4 hover:shadow-md transition-all',
        rec.priority === 'urgent' ? 'border-l-red-500' :
        rec.priority === 'high' ? 'border-l-orange-500' :
        rec.priority === 'medium' ? 'border-l-amber-500' : 'border-l-emerald-500'
      )}
    >
      <CardContent className="p-0">
        <div className="flex flex-col md:grid md:grid-cols-[140px_1fr_auto] gap-0 md:gap-4">
          {/* Lab badge column — full-width strip on mobile, vertical column on desktop */}
          <div className={cn('px-4 py-2.5 md:p-5 bg-gradient-to-br text-white flex md:flex-col items-center md:items-start justify-between gap-3', grad)}>
            <div className="flex items-center gap-2 md:block">
              <p className="text-[10px] uppercase tracking-wider opacity-90 font-semibold">Lab</p>
              <p className="font-heading text-xl md:text-2xl font-bold leading-none">{rec.lab}</p>
            </div>
            <div className="flex items-center gap-1.5">
              <TypeIcon className="h-4 w-4" />
              <span className="text-xs font-semibold">{typeLabel}</span>
            </div>
          </div>

          {/* Body */}
          <div className="p-4 md:p-5 flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-1.5 md:gap-2 mb-2">
              <span className={cn('text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded', pri.bg, pri.text)}>
                {pri.label}
              </span>
              {rec.scheduled_date && (
                <Badge variant="outline" className="text-[10px]"><Calendar className="h-3 w-3 mr-1" /> {rec.scheduled_date}</Badge>
              )}
              <Badge variant="outline" className="text-[10px] font-semibold">{rec.pieces} pcs</Badge>
              <Badge variant="outline" className="text-[10px] font-semibold">{rec.carats.toFixed(2)} ct</Badge>
            </div>
            <p className="text-sm font-medium mb-2">{rec.summary}</p>

            {/* AI fields */}
            {rec.reasoning && (
              <div className="mt-2 p-2.5 rounded-lg bg-violet-50 dark:bg-violet-500/10 border border-violet-200/40 dark:border-violet-500/20">
                <p className="flex items-start gap-1.5 text-xs">
                  <Sparkles className="h-3 w-3 mt-0.5 flex-shrink-0 text-violet-500" />
                  <span className="text-violet-900 dark:text-violet-200">
                    <strong>AI:</strong> {rec.reasoning}
                  </span>
                </p>
                {rec.risk_callout && (
                  <p className="flex items-start gap-1.5 text-xs mt-2 pt-2 border-t border-violet-200/40 dark:border-violet-500/20">
                    <AlertTriangle className="h-3 w-3 mt-0.5 flex-shrink-0 text-amber-500" />
                    <span className="text-amber-700 dark:text-amber-300"><strong>Risk:</strong> {rec.risk_callout}</span>
                  </p>
                )}
                {rec.alternative && (
                  <p className="flex items-start gap-1.5 text-xs mt-2 pt-2 border-t border-violet-200/40 dark:border-violet-500/20">
                    <Lightbulb className="h-3 w-3 mt-0.5 flex-shrink-0 text-blue-500" />
                    <span className="text-blue-700 dark:text-blue-300"><strong>Alternative:</strong> {rec.alternative}</span>
                  </p>
                )}
              </div>
            )}

            {(rec.contributing_customers || []).length > 0 && (
              <p className="text-[11px] text-muted-foreground mt-2">
                <strong>Customers:</strong> {(rec.contributing_customers || []).slice(0, 4).join(' · ')}
                {(rec.contributing_customers || []).length > 4 && ` + ${rec.contributing_customers.length - 4} more`}
              </p>
            )}
          </div>

          {/* Action column */}
          <div className="px-4 pb-4 md:p-5 flex flex-row md:flex-col items-stretch md:items-end justify-end gap-2 md:min-w-[160px] md:border-l md:border-border">
            {isAction && (
              <Button
                size="sm"
                onClick={() => onCreateBooking(rec)}
                className={cn('bg-gradient-to-r text-white font-semibold flex-1 md:w-full', grad)}
                data-testid={`lab-book-${rec.lab}-${rec.type}`}
              >
                <Send className="h-3.5 w-3.5 mr-1" /> Book
              </Button>
            )}
            {(rec.order_ids || []).length > 0 && (
              <Button
                size="sm" variant="outline"
                onClick={() => navigate(`/orders/${rec.order_ids[0]}`)}
                className="flex-1 md:w-full"
              >
                View order
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// ─── Forecast Board (per-lab) ─────────────────────────────────
const ForecastBoard = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ labs: {} });
  const [days, setDays] = useState(14);

  useEffect(() => {
    setLoading(true);
    labApi.forecast(days)
      .then(r => setData(r.data))
      .catch(() => toast.error('Failed to load forecast'))
      .finally(() => setLoading(false));
  }, [days]);

  const labs = Object.keys(data.labs || {});

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <Label className="text-xs">Horizon:</Label>
        <Select value={String(days)} onValueChange={v => setDays(Number(v))}>
          <SelectTrigger className="w-[140px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="7">7 days</SelectItem>
            <SelectItem value="14">14 days</SelectItem>
            <SelectItem value="21">21 days</SelectItem>
            <SelectItem value="30">30 days</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="grid gap-3 grid-cols-1 md:grid-cols-2 lg:grid-cols-4">{[1,2,3,4].map(i => <Skeleton key={i} className="h-64 w-full" />)}</div>
      ) : labs.length === 0 ? (
        <Card><CardContent className="p-12 text-center text-sm text-muted-foreground">
          No open orders with certification labs. Add a lab on Create/Edit Order to see them here.
        </CardContent></Card>
      ) : (
        <div className="grid gap-3 grid-cols-1 md:grid-cols-2 lg:grid-cols-4" data-testid="lab-forecast-grid">
          {labs.map(lab => <LabColumn key={lab} lab={lab} data={data.labs[lab]} />)}
        </div>
      )}
    </div>
  );
};

const LabColumn = ({ lab, data }) => {
  const grad = LAB_GRADIENT[lab] || FALLBACK_GRAD;
  const ring = LAB_RING[lab] || 'ring-slate-300';
  const maxCarats = Math.max(...(data.daily || []).map(d => d.carats), 1);

  return (
    <Card className={cn('overflow-hidden ring-1', ring)} data-testid={`lab-column-${lab}`}>
      <CardHeader className={cn('p-4 bg-gradient-to-br text-white', grad)}>
        <div className="flex items-center justify-between">
          <CardTitle className="font-heading text-xl">{lab}</CardTitle>
          <FlaskConical className="h-5 w-5 opacity-80" />
        </div>
        <div className="mt-2 grid grid-cols-2 gap-2 text-xs">
          <div>
            <p className="opacity-80 uppercase tracking-wider text-[9px] font-semibold">Ready now</p>
            <p className="font-bold tabular-nums">{data.ready_now_pcs} pcs / {data.ready_now_ct.toFixed(2)} ct</p>
          </div>
          <div>
            <p className="opacity-80 uppercase tracking-wider text-[9px] font-semibold">Horizon</p>
            <p className="font-bold tabular-nums">{data.horizon_pcs} pcs / {data.horizon_ct.toFixed(2)} ct</p>
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        {data.daily.length === 0 ? (
          <p className="text-xs text-muted-foreground text-center p-6">No new pieces expected in horizon</p>
        ) : (
          <div className="divide-y divide-border max-h-72 overflow-y-auto">
            {data.daily.map(d => (
              <div key={d.date} className="px-3 py-2">
                <div className="flex items-center justify-between text-xs mb-1">
                  <span className="font-medium tabular-nums">{d.date}</span>
                  <span className="text-muted-foreground">+{d.pieces}p · {d.carats.toFixed(2)}ct</span>
                </div>
                <div className="h-1.5 bg-muted/40 rounded-full overflow-hidden">
                  <div className={cn('h-full bg-gradient-to-r rounded-full', grad)}
                       style={{ width: `${Math.min(100, (d.carats / maxCarats) * 100)}%` }} />
                </div>
                <p className="text-[10px] text-muted-foreground mt-0.5">cum {d.cumulative_carats.toFixed(2)}ct</p>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// ─── Bookings ─────────────────────────────────────────────────
const Bookings = ({ refreshTrigger }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('planned');
  const [labFilter, setLabFilter] = useState('all');

  const load = useCallback(() => {
    setLoading(true);
    const params = {};
    if (status !== 'all') params.status = status;
    if (labFilter !== 'all') params.lab = labFilter;
    labApi.bookings(params)
      .then(r => setItems(r.data.bookings || []))
      .catch(() => toast.error('Failed to load bookings'))
      .finally(() => setLoading(false));
  }, [status, labFilter]);

  useEffect(load, [load, refreshTrigger]);

  const updateStatus = async (b, newStatus) => {
    try {
      await labApi.updateBooking(b.id, { status: newStatus });
      toast.success(`Marked ${newStatus}`);
      load();
    } catch { toast.error('Failed'); }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 flex-wrap">
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-[140px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="planned">Planned</SelectItem>
            <SelectItem value="confirmed">Confirmed</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
            <SelectItem value="cancelled">Cancelled</SelectItem>
            <SelectItem value="all">All</SelectItem>
          </SelectContent>
        </Select>
        <Select value={labFilter} onValueChange={setLabFilter}>
          <SelectTrigger className="w-[120px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All labs</SelectItem>
            {KNOWN_LABS.map(l => <SelectItem key={l} value={l}>{l}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button variant="outline" size="sm" onClick={load} disabled={loading}>
          <RefreshCw className={cn('h-4 w-4 mr-1', loading && 'animate-spin')} /> Refresh
        </Button>
      </div>
      <Card>
        <CardContent className="p-0">
          {loading ? <Skeleton className="h-32 w-full" /> :
           items.length === 0 ? <p className="p-8 text-sm text-muted-foreground text-center">No bookings in this state.</p> :
           <div className="divide-y" data-testid="lab-bookings-list">
             {items.map(b => (
               <div key={b.id} className="px-3 md:px-4 py-3" data-testid={`lab-booking-${b.id}`}>
                 {/* Desktop layout */}
                 <div className="hidden md:grid grid-cols-12 gap-2 items-center text-sm">
                   <div className="col-span-2"><Badge className={cn('text-[10px] bg-gradient-to-br text-white', LAB_GRADIENT[b.lab] || FALLBACK_GRAD)}>{b.lab}</Badge></div>
                   <div className="col-span-4">
                     <p className="font-semibold">{b.type === 'in_house' ? 'In-house visit' : 'Send out'}</p>
                     <p className="text-[11px] text-muted-foreground">{b.scheduled_date}</p>
                   </div>
                   <div className="col-span-3 tabular-nums text-xs">{b.pieces}p · {b.carats?.toFixed?.(2) || b.carats}ct</div>
                   <div className="col-span-3 flex items-center justify-end gap-1">
                     <Badge variant="outline" className="text-[10px] capitalize">{b.status}</Badge>
                     {b.status === 'planned' && (
                       <Button size="sm" variant="ghost" className="h-7 px-2 text-blue-600" title="Confirm" onClick={() => updateStatus(b, 'confirmed')}>
                         <CheckCircle2 className="h-3.5 w-3.5" />
                       </Button>
                     )}
                     {['planned', 'confirmed'].includes(b.status) && (
                       <Button size="sm" variant="ghost" className="h-7 px-2 text-emerald-700" title="Complete" onClick={() => updateStatus(b, 'completed')}>
                         Done
                       </Button>
                     )}
                     {b.status !== 'cancelled' && b.status !== 'completed' && (
                       <Button size="sm" variant="ghost" className="h-7 px-2 text-rose-600" title="Cancel" onClick={() => updateStatus(b, 'cancelled')}>
                         <X className="h-3.5 w-3.5" />
                       </Button>
                     )}
                   </div>
                 </div>
                 {/* Mobile layout */}
                 <div className="md:hidden space-y-2">
                   <div className="flex items-start justify-between gap-2">
                     <div className="min-w-0 flex items-center gap-2">
                       <Badge className={cn('text-[10px] bg-gradient-to-br text-white flex-shrink-0', LAB_GRADIENT[b.lab] || FALLBACK_GRAD)}>{b.lab}</Badge>
                       <div className="min-w-0">
                         <p className="font-semibold text-sm truncate">{b.type === 'in_house' ? 'In-house visit' : 'Send out'}</p>
                         <p className="text-[11px] text-muted-foreground">{b.scheduled_date} · {b.pieces}p · {b.carats?.toFixed?.(2) || b.carats}ct</p>
                       </div>
                     </div>
                     <Badge variant="outline" className="text-[10px] capitalize flex-shrink-0">{b.status}</Badge>
                   </div>
                   {b.status !== 'cancelled' && b.status !== 'completed' && (
                     <div className="flex items-center gap-1.5">
                       {b.status === 'planned' && (
                         <Button size="sm" variant="outline" className="h-8 flex-1 text-xs text-blue-600" onClick={() => updateStatus(b, 'confirmed')}>
                           <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Confirm
                         </Button>
                       )}
                       <Button size="sm" variant="outline" className="h-8 flex-1 text-xs text-emerald-700" onClick={() => updateStatus(b, 'completed')}>
                         Done
                       </Button>
                       <Button size="sm" variant="outline" className="h-8 px-2.5 text-xs text-rose-600" onClick={() => updateStatus(b, 'cancelled')}>
                         <X className="h-3.5 w-3.5" />
                       </Button>
                     </div>
                   )}
                 </div>
               </div>
             ))}
           </div>}
        </CardContent>
      </Card>
    </div>
  );
};

// ─── Settings ─────────────────────────────────────────────────
const LabSettingsPanel = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingLab, setSavingLab] = useState(null);

  useEffect(() => {
    labApi.settings()
      .then(r => setItems(r.data.settings || []))
      .catch(() => toast.error('Failed to load'))
      .finally(() => setLoading(false));
  }, []);

  const updateItem = (idx, key, val) => {
    setItems(prev => prev.map((it, i) => i === idx ? { ...it, [key]: val, _dirty: true } : it));
  };

  const save = async (item) => {
    setSavingLab(item.lab);
    try {
      await labApi.updateLabSettings(item.lab, {
        lab: item.lab,
        in_house_threshold_ct: Number(item.in_house_threshold_ct),
        send_out_turnaround_days: Number(item.send_out_turnaround_days),
        preferred_days: item.preferred_days || [],
        notes: item.notes || '',
      });
      toast.success(`${item.lab} settings saved`);
      setItems(prev => prev.map(it => it.lab === item.lab ? { ...it, _dirty: false, _default: false } : it));
    } catch { toast.error('Failed to save'); }
    finally { setSavingLab(null); }
  };

  if (loading) return <Skeleton className="h-64 w-full" />;

  return (
    <div className="space-y-3">
      {items.map((it, idx) => (
        <Card key={it.lab} data-testid={`lab-settings-${it.lab}`}>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <Badge className={cn('text-sm font-bold bg-gradient-to-br text-white px-2.5 py-1', LAB_GRADIENT[it.lab] || FALLBACK_GRAD)}>{it.lab}</Badge>
              {it._default && <Badge variant="outline" className="text-[10px]">Using defaults</Badge>}
              {it._dirty && <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-700">Unsaved</Badge>}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">In-house threshold (ct)</Label>
                <Input type="number" step="0.1" value={it.in_house_threshold_ct}
                  onChange={e => updateItem(idx, 'in_house_threshold_ct', e.target.value)}
                  data-testid={`lab-${it.lab}-threshold`} />
              </div>
              <div>
                <Label className="text-xs">Send-out turnaround (days)</Label>
                <Input type="number" value={it.send_out_turnaround_days}
                  onChange={e => updateItem(idx, 'send_out_turnaround_days', e.target.value)} />
              </div>
            </div>
            <div className="mt-3 flex justify-end">
              <Button size="sm" disabled={!it._dirty && !it._default || savingLab === it.lab}
                onClick={() => save(it)}
                className={cn('bg-gradient-to-r text-white', LAB_GRADIENT[it.lab] || FALLBACK_GRAD)}
                data-testid={`lab-${it.lab}-save`}>
                {savingLab === it.lab ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null} Save
              </Button>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

// ─── Booking Dialog ──────────────────────────────────────────
const BookingDialog = ({ open, rec, onClose, onSaved }) => {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && rec) {
      setForm({
        lab: rec.lab,
        scheduled_date: rec.scheduled_date || new Date().toISOString().slice(0, 10),
        type: rec.type,
        pieces: rec.pieces,
        carats: rec.carats,
        order_ids: rec.order_ids || [],
        notes: rec.summary || '',
      });
    }
  }, [open, rec]);

  if (!rec) return null;

  const submit = async () => {
    if (!form.scheduled_date) { toast.error('Date required'); return; }
    setSaving(true);
    try {
      await labApi.createBooking(form);
      toast.success('Booking created');
      onSaved();
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="lab-booking-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Badge className={cn('bg-gradient-to-br text-white', LAB_GRADIENT[rec.lab] || FALLBACK_GRAD)}>{rec.lab}</Badge>
            <span>Create {rec.type === 'in_house' ? 'in-house' : 'send-out'} booking</span>
          </DialogTitle>
          <DialogDescription>{rec.summary}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Date *</Label>
              <Input type="date" value={form.scheduled_date || ''} onChange={e => setForm({ ...form, scheduled_date: e.target.value })} data-testid="lab-booking-date" />
            </div>
            <div>
              <Label>Type</Label>
              <Select value={form.type} onValueChange={v => setForm({ ...form, type: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="in_house">In-house visit</SelectItem>
                  <SelectItem value="send_out">Send out</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Pieces</Label>
              <Input type="number" value={form.pieces || 0} onChange={e => setForm({ ...form, pieces: Number(e.target.value) })} />
            </div>
            <div>
              <Label>Carats</Label>
              <Input type="number" step="0.01" value={form.carats || 0} onChange={e => setForm({ ...form, carats: Number(e.target.value) })} />
            </div>
          </div>
          <div>
            <Label>Notes</Label>
            <Textarea value={form.notes || ''} onChange={e => setForm({ ...form, notes: e.target.value })} rows={3} className="text-xs" data-testid="lab-booking-notes" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={saving}
            className={cn('bg-gradient-to-r text-white', LAB_GRADIENT[rec.lab] || FALLBACK_GRAD)}
            data-testid="lab-booking-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Send className="h-4 w-4 mr-1" />} Create
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ─── Main page ───────────────────────────────────────────────
const LabPlanner = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('recommendations');
  const [bookingRec, setBookingRec] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const canAccess = ['admin', 'super_admin', 'production'].includes(user?.role) ||
                    (Array.isArray(user?.tab_permissions) && user.tab_permissions.includes('lab-planner'));

  useEffect(() => {
    if (!canAccess) {
      toast.error("You don't have access to Lab Planner");
      navigate('/');
    }
  }, [canAccess, navigate]);

  if (!canAccess) return null;

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-neutral-950" data-testid="lab-planner-page">
      <Header title="Lab Planner" />
      <div className="px-3 md:px-8 py-3 md:py-5 max-w-screen-2xl mx-auto">
        <Tabs value={tab} onValueChange={setTab}>
          <div className="-mx-3 md:mx-0 mb-3 md:mb-4 px-3 md:px-0 overflow-x-auto scrollbar-hide">
            <TabsList className="inline-flex w-max md:w-auto md:flex-wrap">
              <TabsTrigger value="recommendations" data-testid="lab-tab-recs" className="whitespace-nowrap">
                <Sparkles className="h-3.5 w-3.5 mr-1.5" /> AI Recommendations
              </TabsTrigger>
              <TabsTrigger value="forecast" data-testid="lab-tab-forecast" className="whitespace-nowrap">
                <TrendingUp className="h-3.5 w-3.5 mr-1.5" /> Forecast Board
              </TabsTrigger>
              <TabsTrigger value="bookings" data-testid="lab-tab-bookings" className="whitespace-nowrap">
                <Calendar className="h-3.5 w-3.5 mr-1.5" /> Bookings
              </TabsTrigger>
              <TabsTrigger value="settings" data-testid="lab-tab-settings" className="whitespace-nowrap">
                <SettingsIcon className="h-3.5 w-3.5 mr-1.5" /> Settings
              </TabsTrigger>
            </TabsList>
          </div>
          <TabsContent value="recommendations" key={`r-${refreshKey}`}><Recommendations onCreateBooking={(rec) => setBookingRec(rec)} /></TabsContent>
          <TabsContent value="forecast"><ForecastBoard /></TabsContent>
          <TabsContent value="bookings"><Bookings refreshTrigger={refreshKey} /></TabsContent>
          <TabsContent value="settings"><LabSettingsPanel /></TabsContent>
        </Tabs>
      </div>

      <BookingDialog
        open={!!bookingRec}
        rec={bookingRec}
        onClose={() => setBookingRec(null)}
        onSaved={() => setRefreshKey(k => k + 1)}
      />
    </div>
  );
};

export default LabPlanner;
